import { cn } from "@/lib/utils";
import moeLogoUrl from "@/assets/brand/moe-logo.webp";

interface PageLoaderProps {
  /** Short editorial message, formatted like `Loading · Permissions`. */
  message?: string;
  /** Optional eyebrow above the message (defaults to a brand mark). */
  eyebrow?: string;
  /** Show the MOE logo chip instead of just the spinner ring. */
  withBrand?: boolean;
  className?: string;
}

function PageLoader({
  message = "Loading",
  eyebrow,
  withBrand = false,
  className,
}: PageLoaderProps) {
  return (
    <div
      className={cn(
        "flex min-h-[60vh] flex-col items-center justify-center px-8",
        className,
      )}
    >
      {withBrand && (
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-xl bg-white p-0.5 ring-1 ring-outline-variant/20">
            <img
              src={moeLogoUrl}
              alt="MOE Logo"
              className="h-full w-full object-contain dark:brightness-110"
            />
          </div>
          <div>
            <div className="mb-0.5 flex items-center gap-2">
              <span aria-hidden className="h-px w-5 bg-secondary-500/50" />
              <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-secondary-500">
                Ministry of Education
              </span>
            </div>
            <h1 className="font-display text-[17px] font-bold tracking-tight text-on-surface">
              MOE e-Services
            </h1>
          </div>
        </div>
      )}

      {/* Editorial ringed spinner chip */}
      <div className="relative flex h-12 w-12 items-center justify-center">
        <div className="absolute inset-0 rounded-full bg-secondary-500/5 ring-1 ring-secondary-500/15" />
        <svg
          className="absolute inset-0 h-12 w-12 animate-spin"
          style={{ animationDuration: "1.1s" }}
          viewBox="0 0 48 48"
          fill="none"
          aria-hidden
        >
          <path
            d="M24 4a20 20 0 0 1 20 20"
            stroke="rgb(var(--color-secondary-500))"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
        <span
          aria-hidden
          className="h-1.5 w-1.5 rounded-full bg-secondary-500"
        />
      </div>

      {/* Eyebrow message */}
      <div className="mt-5 flex items-center gap-2">
        <span aria-hidden className="h-px w-5 bg-on-surface-variant/40" />
        <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-on-surface-variant">
          {eyebrow ?? message}
        </span>
      </div>
    </div>
  );
}

export { PageLoader };
export default PageLoader;
